import { ValidationError } from '@/types/validation';
import { TextField, TextFieldProps } from '@mui/material';
import Big from 'big.js';
import React, { FC, useEffect, useState } from 'react';
import { useDebounce } from './Debouncer';

/**
 * There is no official API to get a decimal separator.
 * As an indirect way, this function will try to get the char at index 1
 * from "1.1". This should in most locales result in the correct character.
 * @param lang the locale e.g. "en-US" or "sv-SE"
 * @returns the decimal separator or undefined if none can be found.
 */
const getDecimalSeparator = (lang: string): string | undefined => {
  const formattedNumber = (1.1).toLocaleString(lang);
  return formattedNumber.charAt(1); // The second character is the decimal separator
};

/**
 * Different locales use different decimal separators. Some use "." and some ",".
 * This function will use the browser's locales to know which one is used,
 * and convert them to decimal points (.).
 * @param value The string you want converted.
 * @returns a converted string where the decimal separator always is "."
 */
const convertToCorrectDecimal = (value: string): string => {
  const languages = navigator.languages;
  const decimals = languages.map((lang) => getDecimalSeparator(lang)).filter(Boolean) as string[];
  return decimals.reduce((acc, decimal) => acc.split(decimal).join('.'), value);
};

/**
 * Filters a string to contain only numeric characters and a single decimal point.
 *
 * @param {string} input - The input string to be filtered.
 * @returns {string} - The filtered string containing only numeric characters and at most one decimal point.
 *
 * @example
 * const result = filterToNumericWithOneDecimal("abcdef1.2ghi3.4jklmno");
 * console.log(result); // Output will be "1.234"
 */
const filterToNumericWithOneDecimal = (input: string, numberType?: string) => {
  if (!input) return input;

  const filtered = Array.from(input)
    ?.filter(
      (char, index, arr) =>
        /\d/.test(char) || (char === '.' && arr.indexOf(char) === index) || (char === '-' && index === 0)
    )
    .join('');

  if (numberType === 'integer') {
    return filtered.replace('.', '');
  } else if (filtered === '.') {
    return '0.';
  } else if (filtered === '-.') {
    return '-0.';
  } else {
    return filtered;
  }
};

type NumberTextFieldProps = {
  value: number | string | object | undefined;
  onChange: (value: number | string) => void;
  numberType: string | undefined;
  errors?: ValidationError[];
};

const NumberTextField: FC<NumberTextFieldProps & TextFieldProps> = ({ numberType, ...props }) => {
  const [value, setValue] = useState(props.value);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, val?: string) => {
    if (!props.onChange) return;

    const localValue = val || e.currentTarget.value;
    const type = numberType === 'numeric' ? determineIntegerOrDecimal(localValue) : numberType;
    switch (type) {
      case 'decimal': {
        const decimal = filterToNumericWithOneDecimal(localValue);
        props.onChange(decimal);
        setValue(decimal);
        break;
      }
      default:
      case 'integer': {
        const result = localValue === '-' ? localValue : Number.parseInt(localValue);
        const number = Number.isNaN(result) ? '' : result;
        props.onChange(number);
        setValue(number);
        break;
      }
    }
  };

  const determineIntegerOrDecimal = (value: string): 'integer' | 'decimal' => {
    return value.includes('.') ? 'decimal' : 'integer';
  };

  const parseDecimal = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const unlocaledValue = convertToCorrectDecimal(e.currentTarget.value);
    const onlyOneDecimal = filterToNumericWithOneDecimal(unlocaledValue);
    try {
      // time to use big.js
      Big.strict = true;
      new Big(onlyOneDecimal); // no crash? valid decimal.
      handleChange(e, onlyOneDecimal);
    } catch (error) {
      console.error(error);
      // not a valid decimal. don't return any change.
    }
  };

  const parseInteger = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    try {
      const integer = Number.parseInt(e.currentTarget.value);
      handleChange(e, integer.toString());
    } catch (error) {
      console.error(error);
      // not a valid integer. don't return any change.
    }
  };

  useDebounce(() => {
    const filteredResult = filterToNumericWithOneDecimal(value as string, numberType);
    if (filteredResult !== value) {
      setValue(props.value);
      return;
    }

    props.onChange(value);
  }, [value]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  return (
    <TextField
      {...props}
      error={Boolean(props.errors?.length || (props.required && props.value === ''))}
      value={value ?? ''}
      onChange={(e) => {
        const onlyNumeric = filterToNumericWithOneDecimal(e.currentTarget.value, numberType);
        if (onlyNumeric && onlyNumeric !== '-') {
          switch (numberType) {
            case 'integer':
              parseInteger(e);
              break;
            case 'numeric':
            case 'decimal':
            default:
              parseDecimal(e);
              break;
          }
        } else {
          handleChange(e, onlyNumeric);
        }
      }}
      InputProps={{ ...props.InputProps, autoComplete: 'off' }}
    />
  );
};

export default NumberTextField;
